// protectionTiers.js — the client-facing wording for each Automotive
// protection tier: label, longevity, guarantee, and the exclusions shown
// beneath whichever guarantee applies. The ONE source for this text: the
// calculator picker, the PDF and View Full Quote / Send to Client all read
// it. Keys match the saved quote's `protection` value (unchanged, so saved
// quotes still load). Prices and labour hours stay with the calculator's
// AUTO_PROTECTION.

// Years of annual inspections (with free re-coating of chips) included with
// IGL Aegis.
export const AEGIS_GUARANTEE_YEARS = 3;
// Free inspections included with the Dinitrol Full System, one per year
// from 12 months.
export const DINITROL_FREE_INSPECTIONS = 1;

const dinitrolInspections = DINITROL_FREE_INSPECTIONS === 1
  ? "a free inspection at 12 months"
  : `free annual inspections for the first ${DINITROL_FREE_INSPECTIONS} years`;

// glance: the one-line guarantee for the quote's "At a glance" box.
export const PROTECTION_TIERS = {
  none: { label: "None", longevity: "", guarantee: "" },
  lanoguard: {
    label: "Lanoguard Clear",
    longevity: "Annual reapplication recommended",
    glance: "Clear coating, so the metal stays visible; annual top-up recommended before winter.",
    guarantee: "Lanoguard is a lanolin-based, flexible coating that stays clear, so the metal underneath can be inspected. It is designed to be topped up every year, and we recommend booking an annual top-up before winter.",
  },
  biltHamber: {
    label: "Bilt Hamber Dynax-UB",
    longevity: "Typically 1 to 2 years before a top-up in high-wear areas",
    glance: "Self-healing wax; inspection recommended at 12 months, top-ups in high-wear areas every 1 to 2 years.",
    guarantee: "Dynax-UB is a self-healing wax that reseals minor marks on its own. High-wear areas such as wheel arches and leading edges usually need a top-up every 1 to 2 years. We recommend an inspection at 12 months.",
  },
  dinitrol: {
    label: "Dinitrol Full System",
    longevity: "Multi-year protection, maintained by annual inspection",
    glance: `Includes ${dinitrolInspections}, with any chipped or worn areas touched up at no charge.`,
    guarantee: `Includes ${dinitrolInspections}. Any chipped or worn areas found are touched up at no charge.`,
  },
  ceramic: {
    label: "IGL Aegis Anti-Corrosion Coating",
    longevity: "Long-term protection, maintained by annual inspection",
    glance: `Annual inspection for the first ${AEGIS_GUARANTEE_YEARS} years, with any chips re-coated at no charge.`,
    guarantee: `IGL Aegis cures to a hard, clear film, so the metal stays visible and any change is easy to spot. Like any hard coating, it can be chipped by stones and road debris. Unlike wax or oil-based systems, it does not flow back over a chip on its own. For that reason, Aegis includes an annual inspection for the first ${AEGIS_GUARANTEE_YEARS} years. Any chips found are cleaned and re-coated at no charge, provided the vehicle attends each yearly inspection.`,
  },
};

export const PROTECTION_EXCLUSIONS = "Excludes accident or impact damage beyond normal road stone chips, off-road or competition use, areas repaired or disturbed by others, and corrosion originating from untreated sections.";

// The tier for a saved `protection` value, or null for none/unknown.
export function getProtectionTier(key) {
  if (!key || key === "none") return null;
  return PROTECTION_TIERS[key] || null;
}

const esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// "Protection & Guarantee" section for the client quote (both quote paths),
// using the templates' existing .sec / .sectitle styles. "" when no
// protection is selected.
export function protectionGuaranteeHTML(key) {
  const tier = getProtectionTier(key);
  if (!tier) return "";
  return `<div class="sec">
    <div class="sectitle">Protection &amp; Guarantee</div>
    <p style="font-size:13px;font-weight:600;color:#0e1520;margin:0 0 2px;">${esc(tier.label)}</p>
    ${tier.longevity ? `<p style="font-size:11px;color:#5a6a7a;margin:0 0 8px;">${esc(tier.longevity)}</p>` : ""}
    ${tier.guarantee ? `<p style="font-size:12px;color:#2a3444;line-height:1.7;margin:0 0 8px;">${esc(tier.guarantee)}</p>` : ""}
    <p style="font-size:10.5px;color:#8a95a3;line-height:1.6;margin:0;">${esc(PROTECTION_EXCLUSIONS)}</p>
  </div>`;
}

// The T&Cs sentence about protection longevity - only when protection is selected.
export function protectionTermsSentence(key) {
  return getProtectionTier(key) ? " Protection system longevity is indicative and subject to vehicle use and storage conditions." : "";
}
